import { Array, Filter, HashSet, Order, pipe, Result } from 'effect'

import { type Outcome, toOutcome } from '../outcome.ts'

// ELT が同期した salesforce_user_role の 1 行
export interface SourceRole {
  readonly id: string
  readonly name: string
  readonly parentRoleId: string | null
}

// 書き込み順 (root から葉へ) を決めたロール。depth 0 が root
export interface OrderedSourceRole {
  readonly id: string
  readonly name: string
  readonly parentRoleId: string | null
  readonly depth: number
}

export type SalescoreRoleTreeFailure =
  | { readonly kind: 'empty' }
  | { readonly kind: 'duplicateRoleId'; readonly roleIds: Array.NonEmptyReadonlyArray<string> }
  | { readonly kind: 'missingParent'; readonly roleIds: Array.NonEmptyReadonlyArray<string> }
  | { readonly kind: 'cycle'; readonly roleIds: Array.NonEmptyReadonlyArray<string> }

// UserRole は複合項目を持たないため ELT が Bulk API (CSV) で取得し、CSV には null の表現が無いので
// root の parent_role_id は NULL でなく '' で入る。'' を親欠落と判定すると全 org で同期が必ず fail する
const normalizeParentRoleId = (parentRoleId: string | null): string | null =>
  parentRoleId === '' ? null : parentRoleId

const byId = Order.mapInput(Order.String, ({ id }: SourceRole) => id)

const sortedIds = (roles: readonly SourceRole[]): readonly string[] =>
  pipe(
    roles,
    Array.sort(byId),
    Array.map(({ id }) => id),
  )

const placeLevels = (
  remaining: readonly SourceRole[],
  placedIds: HashSet.HashSet<string>,
  depth: number,
): Result.Result<readonly OrderedSourceRole[], SalescoreRoleTreeFailure> => {
  if (!Array.isReadonlyArrayNonEmpty(remaining)) {
    return Result.succeed([])
  }
  const [blocked, ready] = Array.partition(
    remaining,
    Filter.fromPredicate(
      ({ parentRoleId }: SourceRole) => parentRoleId !== null && !HashSet.has(placedIds, parentRoleId),
    ),
  )
  if (!Array.isReadonlyArrayNonEmpty(ready)) {
    // 親はすべて実在する (missingParent を先に弾いている) のに置けない = 互いを親にしている
    const roleIds = sortedIds(blocked)
    return Array.isReadonlyArrayNonEmpty(roleIds) ? Result.fail({ kind: 'cycle', roleIds }) : Result.succeed([])
  }
  const level = pipe(
    ready,
    Array.sort(byId),
    Array.map((role): OrderedSourceRole => ({ ...role, depth })),
  )
  return Result.map(
    placeLevels(
      blocked,
      pipe(
        ready,
        Array.map(({ id }) => id),
        Array.reduce(placedIds, (set, id) => HashSet.add(set, id)),
      ),
      depth + 1,
    ),
    (rest) => [...level, ...rest],
  )
}

const placeRoles = (
  roles: readonly SourceRole[],
): Result.Result<Array.NonEmptyReadonlyArray<OrderedSourceRole>, SalescoreRoleTreeFailure> => {
  const normalized = roles.map((role) => ({ ...role, parentRoleId: normalizeParentRoleId(role.parentRoleId) }))
  if (!Array.isReadonlyArrayNonEmpty(normalized)) {
    return Result.fail({ kind: 'empty' })
  }
  const duplicateIds = pipe(
    normalized,
    Array.groupBy(({ id }) => id),
    (groups) => Object.values(groups).filter((group) => group.length > 1),
    Array.flatMap((group) => Array.take(group, 1)),
    sortedIds,
  )
  if (Array.isReadonlyArrayNonEmpty(duplicateIds)) {
    return Result.fail({ kind: 'duplicateRoleId', roleIds: duplicateIds })
  }
  const ids = HashSet.fromIterable(normalized.map(({ id }) => id))
  const missingParentIds = pipe(
    normalized,
    Array.filter(({ parentRoleId }) => parentRoleId !== null && !HashSet.has(ids, parentRoleId)),
    sortedIds,
  )
  if (Array.isReadonlyArrayNonEmpty(missingParentIds)) {
    return Result.fail({ kind: 'missingParent', roleIds: missingParentIds })
  }
  return Result.flatMap(placeLevels(normalized, HashSet.empty(), 0), (ordered) =>
    Array.isReadonlyArrayNonEmpty(ordered) ? Result.succeed(ordered) : Result.fail({ kind: 'empty' }),
  )
}

// SF のロール行から、FK を満たす書き込み順 (親が先) の一覧を作る。同じ depth の中は id 順で、同一入力なら
// 同一の並びになる。1 行でも親を解決できなければ全体を失敗にする (部分的な木を書くと E4' の評価が歪む)
export const buildSalescoreRoleTree = (
  roles: readonly SourceRole[],
): Outcome<Array.NonEmptyReadonlyArray<OrderedSourceRole>, SalescoreRoleTreeFailure> => toOutcome(placeRoles(roles))
